import { BlockBlobClient } from '@azure/storage-blob'
import { HttpStatusCode } from 'axios'
import { UploadStatus } from '../utils/constants'
import { apiClient } from '../api/configs/axios-setup'

type ProgressCallback = (filePath: string, percent: number) => void

export type FileUpload = {
  file: File
  blobName: string
  abortController?: AbortController
}

export type UploadResult = {
  blobName: string
  success: boolean
  error?: string
}

export type DownloadResult = {
  blobName: string
  success: boolean
  blob?: Blob
  error?: string
}

const MAX_CONCURRENT_UPLOADS: number = 3

/**
 * Request a SAS URL from the server
 *
 * @param generateSasUrl API function call to get SAS-appended URL String
 * @param uploadType Specifies where to upload for server-side processing
 * @param blobName The name of the file to upload
 * @param aeroTempRunId Used for copying files from a temp to permanent directory
 * @param gmid For storing temp files in a logical manner
 * @returns
 */
async function GetSasUrlFromServer(
  generateSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  blobName: string,
  aeroTempRunId?: string | null,
  gmid?: string
): Promise<string> {
  const dataToPost = JSON.stringify({
    uploadType,
    blobName,
    aeroTempRunId,
    gmid
  })

  console.log('[BlobUploadService] GetSasUrlFromServer - Requesting SAS URL', {
    uploadType,
    blobName,
    aeroTempRunId,
    gmid,
    timestamp: new Date().toISOString()
  })

  let data: string = ''
  // Add timeout to prevent hanging forever if server is down
  const response = (await Promise.race([
    generateSasUrl({
      data: dataToPost
    }),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout - server may not be running')), 30000)
    )
  ])) as any

  console.log('[BlobUploadService] GetSasUrlFromServer - Response received', {
    status: response?.status,
    hasData: !!response?.data,
    hasSasUrl: !!response?.data?.sasUrl,
    blobName
  })

  if (response && response.status === HttpStatusCode.Ok) {
    data = response.data.sasUrl
    console.log('[BlobUploadService] GetSasUrlFromServer - SAS URL extracted successfully', {
      urlLength: data.length,
      blobName
    })
  } else {
    console.error('[BlobUploadService] GetSasUrlFromServer - Failed to get SAS URL', {
      status: response?.status,
      blobName
    })
  }

  return data
}

/**
 * Batch request SAS URLs from the server for multiple files
 *
 * @param generateBatchSasUrl API function call to get multiple SAS URLs
 * @param uploadType Specifies where to upload for server-side processing
 * @param blobNames Array of blob names to get SAS URLs for
 * @param aeroTempRunId Used for copying files from a temp to permanent directory
 * @param gmid For storing temp files in a logical manner
 * @returns Map of blobName -> sasUrl
 */
async function GetBatchSasUrlsFromServer(
  generateBatchSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  blobNames: string[],
  aeroTempRunId?: string | null,
  gmid?: string
): Promise<Map<string, string>> {
  const files = blobNames.map(blobName => ({ uploadType, blobName }))

  const dataToPost = JSON.stringify({
    files,
    aeroTempRunId,
    gmid
  })

  console.log('[BlobUploadService] GetBatchSasUrlsFromServer - Requesting batch SAS URLs', {
    uploadType,
    fileCount: blobNames.length,
    aeroTempRunId,
    gmid,
    timestamp: new Date().toISOString()
  })

  // Add timeout to prevent hanging forever if server is down
  const response = (await Promise.race([
    generateBatchSasUrl({
      data: dataToPost
    }),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout - server may not be running')), 30000)
    )
  ])) as any

  console.log('[BlobUploadService] GetBatchSasUrlsFromServer - Response received', {
    status: response?.status,
    hasData: !!response?.data,
    urlCount: response?.data?.sasUrls?.length || 0
  })

  const sasUrlMap = new Map<string, string>()

  if (response && response.status === HttpStatusCode.Ok && response.data?.sasUrls) {
    response.data.sasUrls.forEach((item: { blobName: string; sasUrl: string }) => {
      sasUrlMap.set(item.blobName, item.sasUrl)
    })
    console.log('[BlobUploadService] GetBatchSasUrlsFromServer - SAS URLs mapped successfully', {
      mapSize: sasUrlMap.size
    })
  } else {
    console.error('[BlobUploadService] GetBatchSasUrlsFromServer - Failed to get batch SAS URLs', {
      status: response?.status
    })
  }

  return sasUrlMap
}

/**
 * Upload a file directly to Azure Blob Storage using a SAS URL obtained from the server
 *
 * @param generateSasUrl API function call to get SAS-appended URL String
 * @param uploadType Specifies where to upload for server-side processing
 * @param blobName The name of the file to upload
 * @param file The raw file to upload
 * @param aeroTempRunId Used for copying files from a temp to permanent directory
 * @param gmid For storing temp files in a logical manner
 * @param onProgress Handler for updating the progress bar for frontend
 * @param abortSignal Signal to stopping the upload process
 */
export async function uploadFileDirectToBlob(
  generateSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  blobName: string,
  file: File,
  aeroTempRunId: string | null,
  gmid?: string,
  onProgress?: ProgressCallback,
  abortSignal?: AbortSignal
): Promise<void> {
  // Get SAS URL from server
  const sasUrl = await GetSasUrlFromServer(
    generateSasUrl,
    uploadType,
    blobName,
    aeroTempRunId,
    gmid
  )
  // Upload using SAS URL (no authentication needed - it's embedded in the URL)
  const blockBlobClient = new BlockBlobClient(sasUrl)

  // Use uploadData with proper options for progress tracking
  await blockBlobClient.uploadData(file, {
    blockSize: 4 * 1024 * 1024, // 4MB blocks
    maxSingleShotSize: 4 * 1024 * 1024, // Force block upload for files > 4MB for granular progress
    concurrency: 20, // parallel uploads
    abortSignal: abortSignal,
    onProgress: ev => {
      if (onProgress && ev.loadedBytes !== undefined) {
        const percent = file.size ? Math.round((ev.loadedBytes / file.size) * 100) : 0
        // Use blobName for progress callback so it matches the file path in the UI
        onProgress(blobName, percent)
      }
    }
  })
}

/**
 * Upload a file directly to Azure Blob Storage using a pre-fetched SAS URL
 * This bypasses the SAS URL request step for better performance in batch operations
 *
 * @param sasUrl Pre-fetched SAS URL
 * @param blobName The name of the file (for progress tracking)
 * @param file The raw file to upload
 * @param onProgress Handler for updating the progress bar for frontend
 * @param abortSignal Signal to stopping the upload process
 */
async function uploadFileWithSasUrl(
  sasUrl: string,
  blobName: string,
  file: File,
  onProgress?: ProgressCallback,
  abortSignal?: AbortSignal
): Promise<void> {
  console.log(`[BlobUploadService] Starting upload for: ${blobName} (${file.size} bytes)`)
  const blockBlobClient = new BlockBlobClient(sasUrl)

  await blockBlobClient.uploadData(file, {
    blockSize: 8 * 1024 * 1024, // 8MB blocks for better throughput on large files
    concurrency: 20, // parallel block uploads
    abortSignal: abortSignal,
    onProgress: ev => {
      if (onProgress && ev.loadedBytes !== undefined) {
        const percent = file.size ? Math.round((ev.loadedBytes / file.size) * 100) : 0
        onProgress(blobName, percent)
      }
    }
  })
  console.log(`[BlobUploadService] Upload completed for: ${blobName}`)
}

/**
 * Start a direct-to-blob upload of a per-case sim template and return the permanent
 * directory URL synchronously (before the upload finishes) so the caller can attach the
 * reference to the case immediately, while the (potentially multi-GB) transfer continues
 * in the background via the returned promise.
 *
 * @returns directoryUrl (…/sim-templates/{uploadId}/ with trailing slash) and the
 *          background uploadPromise (resolves when the transfer completes).
 */
export async function startSimTemplateUpload(
  generateSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  blobName: string,
  file: File,
  onProgress?: ProgressCallback,
  abortSignal?: AbortSignal
): Promise<{ directoryUrl: string; uploadPromise: Promise<void> }> {
  const sasUrl = await GetSasUrlFromServer(generateSasUrl, uploadType, blobName, null)
  if (!sasUrl) {
    throw new Error('Failed to obtain an upload URL from the server.')
  }
  // Derive the blob directory URL (origin + path, minus the query string and filename).
  const url = new URL(sasUrl)
  const fullPath = `${url.origin}${url.pathname}`
  const directoryUrl = fullPath.substring(0, fullPath.lastIndexOf('/') + 1)
  const uploadPromise = uploadFileWithSasUrl(sasUrl, blobName, file, onProgress, abortSignal)
  return { directoryUrl, uploadPromise }
}

export async function deleteFileDirectFromBlob(
  generateSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  blobName: string,
  aeroTempRunId?: string | null,
  gmid?: string
): Promise<void> {
  const sasUrl = await GetSasUrlFromServer(
    generateSasUrl,
    uploadType,
    blobName,
    aeroTempRunId,
    gmid
  )
  const blockBlobClient = new BlockBlobClient(sasUrl)
  await blockBlobClient.deleteIfExists()
}

/**
 * Upload multiple files with concurrency control
 *
 * @param generateSasUrl API function call to get SAS-appended URL String
 * @param uploadType Specifies where to upload for server-side processing
 * @param files Array of files to batch upload to Azure
 * @param aeroTempRunId Used for copying files from a temp to permanent directory
 * @param gmid For storing temp files in a logical manner
 * @param onProgress Handler for updating the progress bar for frontend
 * @param abortSignal Signal to stopping the upload process
 * @returns An array of promises to resolve
 */
export async function uploadMultipleFilesWithConcurrency(
  generateSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  files: FileUpload[],
  aeroTempRunId: string | null,
  gmid?: string,
  onProgress?: ProgressCallback
): Promise<UploadResult[]> {
  const results: UploadResult[] = []
  const executing: Promise<void>[] = []

  for (const file of files) {
    // Use individual abort controller if provided
    const fileAbortSignal = file.abortController?.signal

    // Check if already aborted before starting new upload
    if (fileAbortSignal?.aborted) {
      results.push({
        blobName: file.blobName,
        success: false,
        error: UploadStatus.CANCELLED
      })
      continue
    }

    const promise = uploadFileDirectToBlob(
      generateSasUrl,
      uploadType,
      file.blobName,
      file.file,
      aeroTempRunId,
      gmid,
      onProgress,
      fileAbortSignal
    )
      .then(() => {
        results.push({
          blobName: file.blobName,
          success: true
        })
      })
      .catch(err => {
        console.error('Error uploading file:', err)
        const isCancelled = err.name === 'AbortError' || fileAbortSignal?.aborted
        results.push({
          blobName: file.blobName,
          success: false,
          error: isCancelled ? UploadStatus.CANCELLED : err.message || UploadStatus.FAILED
        })
      })

    const executing_promise = promise.then(() => {
      executing.splice(executing.indexOf(executing_promise), 1)
    })
    executing.push(executing_promise)

    if (executing.length >= MAX_CONCURRENT_UPLOADS) {
      await Promise.race(executing)
    }
  }

  await Promise.all(executing)
  return results
}

/**
 * Upload multiple files with concurrency control using batch SAS URL generation
 * This is optimized to reduce HTTP requests by fetching all SAS URLs in one batch request
 *
 * @param generateBatchSasUrl API function call to get batch SAS URLs
 * @param uploadType Specifies where to upload for server-side processing
 * @param files Array of files to batch upload to Azure
 * @param aeroTempRunId Used for copying files from a temp to permanent directory
 * @param gmid For storing temp files in a logical manner
 * @param onProgress Handler for updating the progress bar for frontend
 * @returns An array of upload results
 */
export async function uploadMultipleFilesWithBatchSas(
  generateBatchSasUrl: (params?: any) => Promise<any>,
  uploadType: string,
  files: FileUpload[],
  aeroTempRunId: string | null,
  gmid?: string,
  onProgress?: ProgressCallback
): Promise<UploadResult[]> {
  const results: UploadResult[] = []

  // Step 1: Pre-fetch all SAS URLs in one batch request
  const blobNames = files.map(f => f.blobName)
  const sasUrlMap = await GetBatchSasUrlsFromServer(
    generateBatchSasUrl,
    uploadType,
    blobNames,
    aeroTempRunId,
    gmid
  )

  // Step 2: Upload all files using pre-fetched SAS URLs with concurrency control
  const executing: Promise<void>[] = []

  for (const file of files) {
    const fileAbortSignal = file.abortController?.signal

    // Check if already aborted before starting new upload
    if (fileAbortSignal?.aborted) {
      results.push({
        blobName: file.blobName,
        success: false,
        error: UploadStatus.CANCELLED
      })
      continue
    }

    // Get pre-fetched SAS URL
    const sasUrl = sasUrlMap.get(file.blobName)
    if (!sasUrl) {
      results.push({
        blobName: file.blobName,
        success: false,
        error: 'Failed to get SAS URL'
      })
      continue
    }

    const promise = uploadFileWithSasUrl(
      sasUrl,
      file.blobName,
      file.file,
      onProgress,
      fileAbortSignal
    )
      .then(() => {
        results.push({
          blobName: file.blobName,
          success: true
        })
      })
      .catch(err => {
        const isCancelled = err.name === 'AbortError' || fileAbortSignal?.aborted
        results.push({
          blobName: file.blobName,
          success: false,
          error: isCancelled ? UploadStatus.CANCELLED : err.message || UploadStatus.FAILED
        })
      })

    const executing_promise = promise.then(() => {
      executing.splice(executing.indexOf(executing_promise), 1)
    })
    executing.push(executing_promise)

    if (executing.length >= MAX_CONCURRENT_UPLOADS) {
      await Promise.race(executing)
    }
  }

  await Promise.all(executing)
  return results
}

/**
 * Download a file directly from Azure Blob Storage via backend proxy (to avoid CORS)
 *
 * @param generateSasUrl API function call (deprecated - not used with proxy)
 * @param downloadType Specifies where to download from (e.g., 'permanent', 'temp')
 * @param blobName The name/path of the file to download
 * @param aeroTempRunId Used for locating files in temp directories
 * @param gmid For locating temp files
 * @param abortSignal Signal to stop the download process
 * @returns The downloaded file as a Blob
 */
export async function downloadFileDirectFromBlob(
  generateSasUrl: (params?: any) => Promise<any>,
  downloadType: string,
  blobName: string,
  aeroTempRunId?: string | null,
  gmid?: string,
  abortSignal?: AbortSignal
): Promise<Blob> {
  console.log('[BlobUploadService] downloadFileDirectFromBlob called', {
    downloadType,
    blobName,
    aeroTempRunId,
    gmid,
    timestamp: new Date().toISOString()
  })

  // Use backend proxy to download blob (avoids CORS issues)
  console.log('[BlobUploadService] Requesting blob download via backend proxy...')

  const response = await apiClient.post(
    '/DownloadBlobProxy',
    {
      uploadType: downloadType,
      blobPath: blobName,
      aeroTempRunId,
      gmid
    },
    {
      responseType: 'blob', // Important: tell axios we expect a blob response
      signal: abortSignal
    }
  )

  console.log('[BlobUploadService] Blob downloaded successfully', {
    size: response.data.size,
    type: response.data.type,
    blobName
  })

  return response.data
}

/**
 * Download a file and trigger browser download
 *
 * @param generateSasUrl API function call to get SAS-appended URL String
 * @param downloadType Specifies where to download from
 * @param blobName The name of the file to download
 * @param fileName Optional custom filename for the download (defaults to blobName)
 * @param aeroTempRunId Used for locating files in temp directories
 * @param gmid For locating temp files
 * @param abortSignal Signal to stop the download process
 */
export async function downloadAndSaveFile(
  generateSasUrl: (params?: any) => Promise<any>,
  downloadType: string,
  blobName: string,
  fileName?: string,
  aeroTempRunId?: string | null,
  gmid?: string,
  abortSignal?: AbortSignal
): Promise<void> {
  const blob = await downloadFileDirectFromBlob(
    generateSasUrl,
    downloadType,
    blobName,
    aeroTempRunId,
    gmid,
    abortSignal
  )

  // Create a download link and trigger it
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || blobName.split('/').pop() || 'download'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Download multiple files with concurrency control
 *
 * @param generateSasUrl API function call to get SAS-appended URL String
 * @param downloadType Specifies where to download from
 * @param blobNames Array of blob names to download
 * @param aeroTempRunId Used for locating files in temp directories
 * @param gmid For locating temp files
 * @returns An array of download results
 */
export async function downloadMultipleFilesWithConcurrency(
  generateSasUrl: (params?: any) => Promise<any>,
  downloadType: string,
  blobNames: string[],
  aeroTempRunId?: string | null,
  gmid?: string,
  abortSignal?: AbortSignal
): Promise<DownloadResult[]> {
  const results: DownloadResult[] = []
  const executing: Promise<void>[] = []

  for (const blobName of blobNames) {
    // Check if already aborted before starting new download
    if (abortSignal?.aborted) {
      results.push({
        blobName: blobName,
        success: false,
        error: 'Download cancelled'
      })
      continue
    }

    const promise = downloadFileDirectFromBlob(
      generateSasUrl,
      downloadType,
      blobName,
      aeroTempRunId,
      gmid,
      abortSignal
    )
      .then(blob => {
        results.push({
          blobName: blobName,
          success: true,
          blob: blob
        })
      })
      .catch(err => {
        const isCancelled = err.name === 'AbortError' || abortSignal?.aborted
        results.push({
          blobName: blobName,
          success: false,
          error: isCancelled ? 'Download cancelled' : err.message || 'Download failed'
        })
      })

    const executing_promise = promise.then(() => {
      executing.splice(executing.indexOf(executing_promise), 1)
    })
    executing.push(executing_promise)

    if (executing.length >= MAX_CONCURRENT_UPLOADS) {
      await Promise.race(executing)
    }
  }

  await Promise.all(executing)
  return results
}

/**
 * List blob files from a specific folder path in Azure Storage
 * Uses backend API to avoid CORS issues with direct Azure Storage listing
 * @param generateSasUrl API function to get SAS URL for container access (kept for interface consistency)
 * @param downloadType The type of download (e.g., 'CASE_LOG_FILE')
 * @param folderPath The folder path to list files from (e.g., 'cases/105/Logs')
 * @param aeroTempRunId Optional temp run ID
 * @param gmid Optional GMID
 * @returns Array of blob file names (just the file names, not full paths)
 */
export async function listBlobsInFolder(
  generateSasUrl: (params?: any) => Promise<any>,
  downloadType: string,
  folderPath: string,
  aeroTempRunId?: string | null,
  gmid?: string
): Promise<string[]> {
  console.log('[BlobUploadService] listBlobsInFolder called', {
    downloadType,
    folderPath,
    timestamp: new Date().toISOString()
  })

  try {
    // Call backend API to list blobs (avoids CORS issues with direct Azure listing)
    console.log('[BlobUploadService] Calling backend ListBlobs API')

    const response = await apiClient.post('/ListBlobs', {
      uploadType: downloadType,
      folderPath: folderPath,
      aeroTempRunId: aeroTempRunId,
      gmid: gmid
    })

    const fileNames = response.data?.files || []

    console.log('[BlobUploadService] Blob listing complete', {
      folderPath,
      fileCount: fileNames.length,
      files: fileNames
    })

    return fileNames
  } catch (error: any) {
    console.error('[BlobUploadService] Error listing blobs', {
      folderPath,
      error: error?.message,
      stack: error?.stack
    })
    throw error
  }
}

export default {
  uploadFileDirectToBlob,
  deleteFileDirectFromBlob,
  uploadMultipleFilesWithConcurrency,
  downloadFileDirectFromBlob,
  downloadAndSaveFile,
  downloadMultipleFilesWithConcurrency,
  listBlobsInFolder
}
